import React from 'react';

import { FIRE_CLASS_OPTIONS } from '@vlabs/api-bindings/src/luna-client/constants';
import { FireIcon } from '@vlabs/icons';
import { calculateProbabilityPercent } from '@vlabs/monokit/shared/lib/utils';
import { useImage } from '@vlabs/shared/hooks';
import { Tag, useWindowSize } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { selectFacecoverVersion } from '@vlapps/point/src/app/store/selectors';

import st from './EventDetailsRenderer.module.sass';
import { FACE_COVER_DISPLAY_BUILDERS } from './FacecoverAnalyticDisplayConfig';
import { ImageBlock } from './ImageBlock';
import { InfoBlock } from './InfoBlock';
import { ScoreBlock } from './ScoreBlock';

const FACE_COVER_EVENT_TYPES = ['facecover', 'balaclava', 'niqab', 'other'];
const isFacecoverEventType = (eventType) => FACE_COVER_EVENT_TYPES.includes(eventType);

const getEventConfig = (event, t, windowSize, facecoverVersion) => {
  const { event_type, event: rawEvent } = event || {};
  const scoreRaw = rawEvent?.[`${event_type}_score`];
  const scorePercent = scoreRaw !== undefined
    ? `${calculateProbabilityPercent(scoreRaw)}%`
    : null;

  if (isFacecoverEventType(event_type)) {
    const builder = FACE_COVER_DISPLAY_BUILDERS[facecoverVersion] ?? FACE_COVER_DISPLAY_BUILDERS.v1;

    return builder(event, t, windowSize.width);
  }

  switch (event?.event_type) {
    case 'fight':
      return {
        score: { value: scorePercent },
        frame: {
          detections: event.event?.fight_bbox ? [event.event?.fight_bbox] : [],
          type: 'bbox',
        },
      };
    case 'weapon':
      return {
        score: { value: scorePercent },
        frame: {
          detections: event.event?.overview?.detection ? [event.event?.overview?.detection] : [],
          type: 'bbox',
        },
      };
    case 'bag':
      return {
        frame: {
          detections: event.event?.overview?.detection ? [event.event?.overview?.detection] : [],
          type: 'bbox',
        },
      };
    case 'fire':
      return {
        score: { value: scorePercent },
        info: {
          extraTags: <Tag
            className={st.FireTag}
            icon={<FireIcon />}
            size={windowSize.width < 1920 ? 'medium' : 'large'}
            value={FIRE_CLASS_OPTIONS?.[event?.event?.fire_class]?.label}
          />,
        },
      };
    case 'lying':
      return {
        score: { value: scorePercent },
        frame: {
          detections: event.event?.overview?.detection ? [event.event?.overview?.detection] : [],
          type: 'bbox',
        },
      };
    case 'handsup':
      return {
        score: { value: scorePercent },
        frame: {
          detections: event.event?.overview?.detection ? [event.event?.overview?.detection] : [],
          type: 'bbox',
        },
      };
    case 'fall':
      return {
        score: { value: scorePercent },
        frame: {
          detections: event.event?.overview?.detection ? [event.event?.overview?.detection] : [],
          type: 'bbox',
        },
      };
    default:
      return {
        score: { value: scorePercent },
      };
  }
};

function EventDetailsRendererComponent({ event, facecoverVersion }) {
  const { t } = useTranslation();
  const windowSize = useWindowSize();

  const config = getEventConfig(event, t, windowSize, facecoverVersion);
  const createTime = event?.create_time;
  const streamId = event?.event?.stream_id;
  const fullFrame = useImage(event?.event?.overview?.image);

  return (
    <div className={st.Root}>
      <InfoBlock
        event={event}
        extraTags={config?.info?.extraTags}
      />
      <div className={st.FrameScoreWrapper}>

        <ImageBlock
          createTime={createTime}
          detections={config?.frame?.detections}
          detectionType={config?.frame?.type}
          eventType={event?.event_type}
          fullFrame={fullFrame}
          streamId={streamId}
        />

        <ScoreBlock
          event={event}
          extraRows={config?.score?.additionalInfo}
          scoreMeta={config?.score?.scoreMeta}
          scoreValue={config?.score?.value}
        />
      </div>
    </div>
  );
}

export const EventDetailsRenderer = connect(
  (state) => ({
    facecoverVersion: selectFacecoverVersion(state),
  }),
)(EventDetailsRendererComponent);
